import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import {
  MessageCircle,
  X,
  Send,
  Loader2,
  Trash2,
  Plus,
  ChevronLeft,
  Scale,
  User,
  Bot,
  History,
  FolderPlus,
  FileText,
  Search,
  CreditCard,
  Compass,
  CalendarDays,
  ChevronRight,
  type LucideIcon,
} from 'lucide-react';
import { chatApi } from '../../services/chatApi';
import { ChatMessage, ConversationThread } from '../../types/chat';
import { cn } from '../../lib/utils';
import { onOpenAssistant, type AssistantEntry } from './assistantBus';

/* ─── Parse feature links from markdown-style links ─── */
const parseContent = (content: string, navigate: (path: string) => void) => {
  // Match [label](/path) patterns
  const parts = content.split(/(\[[^\]]+\]\([^)]+\))/g);
  return parts.map((part, i) => {
    const linkMatch = part.match(/\[([^\]]+)\]\(([^)]+)\)/);
    if (linkMatch) {
      const [, label, path] = linkMatch;
      return (
        <button
          key={i}
          onClick={(e) => { e.stopPropagation(); navigate(path); }}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-legal-green-50 dark:bg-legal-green-900/30 text-legal-green-600 dark:text-legal-green-300 text-xs font-medium hover:bg-legal-green-100 dark:hover:bg-gold-900/50 transition-colors mx-0.5"
        >
          {label}
        </button>
      );
    }
    return <span key={i}>{part}</span>;
  });
};

/* ─── Single Message Bubble ─── */
const MessageBubble: React.FC<{
  message: ChatMessage;
  isStreaming?: boolean;
}> = ({ message, isStreaming }) => {
  const navigate = useNavigate();
  const isUser = message.role === 'user';

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={cn('flex gap-2', isUser ? 'justify-end' : 'justify-start')}
    >
      {!isUser && (
        <div className="w-7 h-7 rounded-full bg-legal-green-50 dark:bg-legal-green-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Scale className="h-3.5 w-3.5 text-legal-green-500 dark:text-legal-green-300" />
        </div>
      )}

      <div
        className={cn(
          'max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed',
          isUser
            ? 'bg-navy-700 text-white dark:bg-navy-600 rounded-br-md'
            : 'bg-surface-inset text-text-primary border border-border-secondary rounded-bl-md'
        )}
      >
        <div className={cn('break-words', isUser ? 'whitespace-pre-wrap' : 'prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-li:my-0.5 prose-headings:mt-2 prose-headings:mb-1')}>
          {isUser ? message.content : (
            <ReactMarkdown
              components={{
                a: ({ href, children }) => {
                  if (href?.startsWith('/')) {
                    return (
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(href); }}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-legal-green-50 dark:bg-legal-green-900/30 text-legal-green-600 dark:text-legal-green-300 text-xs font-medium hover:bg-legal-green-100 dark:hover:bg-legal-green-800/40 transition-colors mx-0.5 no-underline"
                      >
                        {children}
                      </button>
                    );
                  }
                  return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
                },
              }}
            >
              {message.content}
            </ReactMarkdown>
          )}
          {isStreaming && (
            <span className="inline-block w-1.5 h-4 bg-legal-green-500 ml-0.5 animate-pulse rounded-sm" />
          )}
        </div>
      </div>

      {isUser && (
        <div className="w-7 h-7 rounded-full bg-legal-green-50 dark:bg-legal-green-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
          <User className="h-3.5 w-3.5 text-legal-green-500 dark:text-legal-green-300" />
        </div>
      )}
    </motion.div>
  );
};

/* ─── Starting points ───
 * Big, plain-worded jobs a lawyer actually comes here to do. Each card sends a
 * full question, so the assistant answers with the exact screen and steps.
 * Keyed to the active workspace — a civil lawyer must not be offered FIR and
 * bail cards for tools their workspace cannot reach.
 */
interface HelpCard {
  icon: LucideIcon;
  title: string;
  hint: string;
  /** Sent as is. */
  question?: string;
  /** Put in the box for the lawyer to finish (a section number, say), never sent half-written. */
  prefill?: string;
}

const CARD_ADD_CASES: HelpCard = {
  icon: FolderPlus,
  title: 'Add my existing cases',
  hint: 'Bring all your running cases in at once',
  question: 'How do I add all my existing cases to this platform at once?',
};
const CARD_FIND_JUDGMENT: HelpCard = {
  icon: Search,
  title: 'Find a judgment',
  hint: 'Search Supreme Court and High Court rulings',
  question: 'How do I search for a judgment?',
};
const CARD_PLAN: HelpCard = {
  icon: CreditCard,
  title: 'My plan and credits',
  hint: 'Trial days left, renew, invoices',
  question: 'Where can I see my plan, credits and renewal date?',
};
const CARD_TOUR: HelpCard = {
  icon: Compass,
  title: 'What can this platform do?',
  hint: 'A quick tour, in simple words',
  question: 'What features does this platform have?',
};

const CARDS_BY_ROLE: Record<string, HelpCard[]> = {
  criminal: [
    CARD_ADD_CASES,
    {
      icon: FileText,
      title: 'I got a new FIR',
      hint: 'Read it and see the next steps',
      question: 'I received a new FIR. Where do I start on this platform?',
    },
    {
      icon: CalendarDays,
      title: 'Prepare for a hearing',
      hint: 'Bail hearing, brief, documents',
      question: 'How do I prepare for a bail hearing on this platform?',
    },
    CARD_FIND_JUDGMENT,
    CARD_PLAN,
  ],
  civil: [
    CARD_ADD_CASES,
    {
      icon: FileText,
      title: 'My client got a legal notice',
      hint: 'Analyse the notice',
      question: 'My client received a legal notice. Where do I start on this platform?',
    },
    {
      icon: CalendarDays,
      title: 'Check the time left to file',
      hint: 'Time-bar and forum tools',
      question: 'How do I check how much time is left to file a claim?',
    },
    CARD_FIND_JUDGMENT,
    CARD_PLAN,
  ],
  corporate: [
    {
      icon: CalendarDays,
      title: 'Track deadlines',
      hint: 'Compliance and post-closing',
      question: 'Where do I track compliance deadlines and post-closing obligations?',
    },
    {
      icon: FileText,
      title: 'Review the data room',
      hint: 'Documents and findings',
      question: 'How do I review the data room?',
    },
    CARD_TOUR,
  ],
};

// The 0-role "All areas" session — keep it vertical-neutral.
const DEFAULT_CARDS: HelpCard[] = [
  CARD_ADD_CASES,
  {
    icon: FileText,
    title: 'Start a new matter',
    hint: 'Client, case and documents in one place',
    question: 'Which tool should I use to start a new matter?',
  },
  CARD_FIND_JUDGMENT,
  CARD_PLAN,
];

/* ─── Opened from the criminal home's "Something else…" card ───
 * The lawyer has just passed over the home's cards, so these starters cover
 * only what those cards do not, and the box shows what can be asked. Shown for
 * that one fresh chat; the assistant's own button always shows the cards above.
 */
const SOMETHING_ELSE_CARDS: HelpCard[] = [
  {
    icon: Scale,
    title: 'Is this offence bailable?',
    hint: 'Type the section number',
    prefill: 'Is this bailable? BNS section ',
  },
  {
    icon: FileText,
    title: 'Draft a bail application or vakalatnama',
    hint: 'Where to find the drafts',
    question: 'Where do I draft a bail application or a vakalatnama?',
  },
  {
    icon: CalendarDays,
    title: 'Prepare for a hearing',
    hint: 'Brief, points and documents',
    question: 'How do I prepare for my next hearing on this platform?',
  },
  CARD_PLAN,
];
const SOMETHING_ELSE_HINT = 'e.g. Is BNS 318 bailable? · Where do I add a chargesheet?';

const getCards = (): HelpCard[] => {
  try {
    const role = sessionStorage.getItem('nyayabodh_active_role');
    return (role && CARDS_BY_ROLE[role]) || DEFAULT_CARDS;
  } catch {
    return DEFAULT_CARDS;
  }
};

/* ─── Keyboard-safe viewport inset (mobile) ───
 * `position: fixed` anchors to the layout viewport, which iOS/Android keep
 * full-height even once the on-screen keyboard is up — only the *visual*
 * viewport shrinks. Left alone, the bottom-anchored input row ends up
 * hidden behind the keyboard. This tracks the keyboard height via the
 * VisualViewport API and lifts the panel by that amount, capping its
 * max-height to what's actually visible above the keyboard. No-ops to
 * `{ bottom: 0, maxHeight: 0 }` on desktop and on browsers without the API.
 */
// Must match the panel's own bottom-6 (24px); the lifted panel sits this far
// above the visual viewport's bottom edge, same as it does with no keyboard.
const KEYBOARD_INSET_BOTTOM_MARGIN_PX = 24;
const KEYBOARD_INSET_TOP_MARGIN_PX = 16;

const useKeyboardInset = (active: boolean) => {
  const [inset, setInset] = useState({ bottom: 0, maxHeight: 0 });

  useEffect(() => {
    const vv = window.visualViewport;
    if (!active || !vv) {
      setInset({ bottom: 0, maxHeight: 0 });
      return;
    }

    const handleResize = () => {
      const keyboardHeight = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setInset({
        bottom: keyboardHeight,
        // The panel's bottom edge, once lifted, sits BOTTOM_MARGIN above the
        // visible area's bottom — so that same margin is already "spent" and
        // must come off the room left for height, plus a small top margin,
        // or the panel's top edge pokes above the visible viewport.
        maxHeight: keyboardHeight > 0
          ? Math.max(0, vv.height - KEYBOARD_INSET_BOTTOM_MARGIN_PX - KEYBOARD_INSET_TOP_MARGIN_PX)
          : 0,
      });
    };

    handleResize();
    vv.addEventListener('resize', handleResize);
    vv.addEventListener('scroll', handleResize);
    return () => {
      vv.removeEventListener('resize', handleResize);
      vv.removeEventListener('scroll', handleResize);
    };
  }, [active]);

  return inset;
};

/* An advocate, not a chat bubble: head, black gown, and the white neck bands
 * (the one detail that reads as "advocate" at 32px). */
const AdvocateGlyph: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 32 32" className={className} aria-hidden>
    <path d="M4 31c0-7 5-11.5 12-11.5S28 24 28 31z" className="fill-legal-green-900" />
    <circle cx="16" cy="10.5" r="5.5" fill="#fff" />
    <path d="M12.6 19.8h3l-.6 6h-2.4zM16.4 19.8h3l-.6 6h-2.4z" fill="#fff" />
  </svg>
);

/* ─── Main Floating Chat Panel ─── */
const ChatPanel: React.FC = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversations, setConversations] = useState<ConversationThread[]>([]);
  const [currentThreadId, setCurrentThreadId] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  // Read on each open rather than at module load: the lawyer can switch
  // workspace (PracticeAreaSwitcher) without a reload, and the cards must
  // follow the lens they are now in.
  const [cards, setCards] = useState<HelpCard[]>(getCards);
  // Where this fresh chat was opened from; null = the assistant's own button.
  const [entry, setEntry] = useState<AssistantEntry | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const keyboardInset = useKeyboardInset(isOpen);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingText, scrollToBottom]);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      setCards(getCards());
    }
  }, [isOpen]);

  const loadConversations = async () => {
    try {
      const convos = await chatApi.listConversations();
      setConversations(convos);
    } catch {
      // API not connected
    }
  };

  const loadConversation = async (threadId: string) => {
    try {
      const detail = await chatApi.getConversation(threadId);
      setMessages(detail.messages);
      setCurrentThreadId(threadId);
      setShowHistory(false);
    } catch {
      // Failed to load
    }
  };

  const handleNewConversation = () => {
    setMessages([]);
    setCurrentThreadId(null);
    setShowHistory(false);
    setStreamingText('');
  };

  const handleDeleteConversation = async (threadId: string) => {
    try {
      await chatApi.deleteConversation(threadId);
      setConversations(prev => prev.filter(c => c.id !== threadId));
      if (currentThreadId === threadId) handleNewConversation();
    } catch {
      // Failed
    }
  };

  // `threadOverride` lets a caller that just reset the thread send on the fresh
  // (null) thread instead of the stale `currentThreadId` this closure captured.
  const handleSend = async (text?: string, threadOverride?: string | null) => {
    const messageText = text || input.trim();
    if (!messageText || isStreaming) return;
    const threadId = threadOverride === undefined ? currentThreadId : threadOverride;

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      thread_id: threadId || '',
      role: 'user',
      content: messageText,
      created_at: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsStreaming(true);
    setStreamingText('');

    let fullText = '';

    await chatApi.sendMessage(
      messageText,
      threadId,
      // onToken
      (tokenText) => {
        fullText += tokenText;
        setStreamingText(fullText);
      },
      // onDone
      (data) => {
        const assistantMessage: ChatMessage = {
          id: data.message_id,
          thread_id: data.thread_id,
          role: 'assistant',
          content: fullText,
          created_at: new Date().toISOString(),
        };
        setMessages(prev => [...prev, assistantMessage]);
        setCurrentThreadId(data.thread_id);
        setStreamingText('');
        setIsStreaming(false);
      },
      // onError
      () => {
        const errorMessage: ChatMessage = {
          id: crypto.randomUUID(),
          thread_id: threadId || '',
          role: 'assistant',
          content: "Sorry, I couldn't connect just now. Please try again in a moment.",
          created_at: new Date().toISOString(),
        };
        setMessages(prev => [...prev, errorMessage]);
        setStreamingText('');
        setIsStreaming(false);
      },
    );
  };

  // Another page opened the assistant (the home's "Something else…"): open on
  // a NEW chat with that entry's starters and the cursor in the box. The ref
  // keeps the single window listener pointed at this render's handlers. A reply
  // still streaming is never cut off: the fresh chat waits for it to end.
  const pendingEntryRef = useRef<AssistantEntry | null>(null);
  const openFromRef = useRef<(e: AssistantEntry) => void>(() => {});
  openFromRef.current = (from: AssistantEntry) => {
    setIsOpen(true);
    if (isStreaming) {
      pendingEntryRef.current = from;
      return;
    }
    handleNewConversation();
    setEntry(from);
    setInput('');
    inputRef.current?.focus();
  };
  useEffect(() => onOpenAssistant((from) => openFromRef.current(from)), []);
  useEffect(() => {
    if (isStreaming || pendingEntryRef.current === null) return;
    const from = pendingEntryRef.current;
    pendingEntryRef.current = null;
    openFromRef.current(from);
  }, [isStreaming]);

  const closePanel = () => {
    setIsOpen(false);
    setEntry(null);
  };
  const startCard = (card: HelpCard) => {
    if (card.prefill) {
      setInput(card.prefill);
      inputRef.current?.focus();
    } else if (card.question) {
      handleSend(card.question);
    }
  };
  const welcomeCards = entry === 'something_else' ? SOMETHING_ELSE_CARDS : cards;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Floating Toggle Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            onClick={() => { setEntry(null); setIsOpen(true); loadConversations(); }}
            className="fixed bottom-6 right-6 z-50 flex items-center gap-3 group"
            aria-label="Ask the iAdvocate assistant how to use this platform"
          >
            {/* Says what the button is for — a bare icon reads as WhatsApp. */}
            <span className="hidden sm:block max-w-[200px] rounded-xl bg-surface-elevated border border-border-primary px-3 py-2 text-left text-xs leading-snug text-text-secondary shadow-lg">
              Any doubt? Ask me how to use this platform.
            </span>
            <span className="relative w-14 h-14 rounded-full bg-gradient-to-br from-legal-green-500 to-legal-green-400 shadow-lg group-hover:shadow-xl group-hover:scale-105 group-active:scale-95 transition-all duration-200 flex items-center justify-center">
              <AdvocateGlyph className="h-8 w-8" />
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            className="fixed bottom-6 left-4 right-4 sm:left-auto sm:right-6 z-50 w-auto sm:w-[400px] h-[70vh] sm:h-[600px] max-h-[80vh] rounded-2xl bg-surface-elevated border border-border-primary shadow-dark-elevation-4 flex flex-col overflow-hidden"
            style={
              keyboardInset.bottom > 0
                ? {
                    bottom: `${keyboardInset.bottom + KEYBOARD_INSET_BOTTOM_MARGIN_PX}px`,
                    maxHeight: `${keyboardInset.maxHeight}px`,
                  }
                : undefined
            }
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border-secondary bg-surface-elevated">
              <div className="flex items-center gap-2">
                {showHistory ? (
                  <button
                    onClick={() => setShowHistory(false)}
                    className="min-h-[44px] min-w-[44px] -ml-2 flex items-center justify-center text-text-tertiary hover:text-text-primary hover:bg-surface-inset rounded-lg transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                ) : (
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-legal-green-500 to-legal-green-400">
                    <AdvocateGlyph className="h-6 w-6" />
                  </span>
                )}
                <div>
                  <h3 className="text-sm font-semibold text-text-primary">
                    {showHistory ? 'Earlier chats' : 'Help desk'}
                  </h3>
                  {!showHistory && (
                    <p className="text-xs text-text-tertiary">Ask me how to use iAdvocate</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                {!showHistory && (
                  <>
                    <button
                      onClick={() => { setShowHistory(true); loadConversations(); }}
                      className="min-h-[44px] min-w-[44px] flex items-center justify-center text-text-tertiary hover:text-text-primary hover:bg-surface-inset rounded-lg transition-colors"
                      aria-label="Earlier chats"
                      title="Earlier chats"
                    >
                      <History className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => { setEntry(null); handleNewConversation(); }}
                      className="min-h-[44px] min-w-[44px] flex items-center justify-center text-text-tertiary hover:text-text-primary hover:bg-surface-inset rounded-lg transition-colors"
                      aria-label="Start a new chat"
                      title="Start a new chat"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </>
                )}
                <button
                  onClick={closePanel}
                  className="min-h-[44px] min-w-[44px] -mr-2 flex items-center justify-center text-text-tertiary hover:text-text-primary hover:bg-surface-inset rounded-lg transition-colors"
                  aria-label="Close help desk"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Content */}
            {showHistory ? (
              /* Conversation History */
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {conversations.length > 0 ? (
                  conversations.map(convo => (
                    <div
                      key={convo.id}
                      className={cn(
                        'flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-all duration-200 group',
                        convo.id === currentThreadId
                          ? 'bg-gold-50 dark:bg-gold-900/10 border-gold-200 dark:border-gold-800'
                          : 'border-border-secondary hover:bg-surface-inset'
                      )}
                      onClick={() => loadConversation(convo.id)}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-text-primary truncate">{convo.title || 'New conversation'}</p>
                        {convo.last_message && (
                          <p className="text-xs text-text-tertiary truncate mt-0.5">{convo.last_message}</p>
                        )}
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteConversation(convo.id); }}
                        className="p-1 text-text-tertiary hover:text-crimson-500 opacity-0 group-hover:opacity-100 transition-all"
                        aria-label="Delete conversation"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12">
                    <MessageCircle className="h-8 w-8 text-text-tertiary mx-auto mb-2" />
                    <p className="text-sm text-text-secondary">No earlier chats yet</p>
                  </div>
                )}
              </div>
            ) : (
              /* Chat Messages */
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
                {messages.length === 0 && !isStreaming ? (
                  /* Welcome State */
                  <div className="flex flex-col h-full py-1">
                    <h4 className="text-lg font-semibold text-text-primary">
                      {entry === 'something_else' ? 'What do you need?' : 'Hi! How can I help?'}
                    </h4>
                    <p className="text-sm text-text-secondary mt-1 mb-3">
                      {entry === 'something_else'
                        ? 'Type it in your own words below, or start with one of these.'
                        : 'Tap a topic, or type your own question below. I will tell you exactly where to click.'}
                    </p>
                    <div className="space-y-2">
                      {welcomeCards.map(card => (
                        <button
                          key={card.title}
                          onClick={() => startCard(card)}
                          className="w-full flex items-center gap-3 text-left px-3 py-2.5 min-h-[52px] rounded-xl border border-border-secondary bg-surface-elevated hover:bg-surface-inset hover:border-legal-green-400/40 active:scale-[0.99] transition-all duration-150 group"
                        >
                          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-legal-green-50 dark:bg-legal-green-900/30">
                            <card.icon className="h-5 w-5 text-legal-green-600 dark:text-legal-green-300" aria-hidden />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-medium text-text-primary">{card.title}</span>
                            <span className="block text-xs text-text-tertiary">{card.hint}</span>
                          </span>
                          <ChevronRight className="h-4 w-4 text-text-tertiary group-hover:text-text-primary" aria-hidden />
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => startCard(CARD_TOUR)}
                      className="mt-3 self-start text-xs font-medium text-legal-green-600 dark:text-legal-green-300 hover:underline"
                    >
                      Or show me everything this platform can do
                    </button>
                  </div>
                ) : (
                  <>
                    {messages.map(msg => (
                      <MessageBubble key={msg.id} message={msg} />
                    ))}
                    {isStreaming && streamingText && (
                      <MessageBubble
                        message={{
                          id: 'streaming',
                          thread_id: '',
                          role: 'assistant',
                          content: streamingText,
                          created_at: new Date().toISOString(),
                        }}
                        isStreaming
                      />
                    )}
                    {isStreaming && !streamingText && (
                      <div className="flex gap-2 items-start">
                        <div className="w-7 h-7 rounded-full bg-legal-green-50 dark:bg-legal-green-900/30 flex items-center justify-center">
                          <Scale className="h-3.5 w-3.5 text-legal-green-500 dark:text-legal-green-300" />
                        </div>
                        <div className="flex items-center gap-1.5 px-3 py-2.5 rounded-2xl bg-surface-inset border border-border-secondary rounded-bl-md">
                          <div className="flex gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-legal-green-500 animate-bounce" style={{ animationDelay: '0ms' }} />
                            <span className="w-1.5 h-1.5 rounded-full bg-legal-green-500 animate-bounce" style={{ animationDelay: '150ms' }} />
                            <span className="w-1.5 h-1.5 rounded-full bg-legal-green-500 animate-bounce" style={{ animationDelay: '300ms' }} />
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>
            )}

            {/* Input */}
            {!showHistory && (
              <div className="px-4 py-3 border-t border-border-secondary bg-surface-elevated">
                <div className="flex items-center gap-2">
                  <input
                    ref={inputRef}
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={
                      entry === 'something_else' && messages.length === 0
                        ? SOMETHING_ELSE_HINT
                        : 'Type your question here…'
                    }
                    aria-label="Your question"
                    disabled={isStreaming}
                    className="flex-1 bg-surface-inset border border-border-primary rounded-xl px-3 py-2.5 text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-legal-green-500 focus:border-transparent disabled:opacity-50"
                  />
                  <button
                    onClick={() => handleSend()}
                    disabled={!input.trim() || isStreaming}
                    className={cn(
                      'p-2.5 rounded-xl transition-all duration-200',
                      input.trim() && !isStreaming
                        ? 'bg-legal-green-500 text-white hover:bg-legal-green-400 active:scale-95'
                        : 'bg-surface-inset text-text-tertiary cursor-not-allowed'
                    )}
                    aria-label="Send message"
                  >
                    {isStreaming ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <p className="text-[9px] text-text-tertiary mt-1.5 text-center">
                  I am an AI helper for using iAdvocate. For the law, always check the source.
                </p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatPanel;
